import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import { supabase } from '../supabase.js';

const CATEGORIES = [
  { key: '🍷 Food & Wine', subs: ['🍾 Wine & Drinks', '🍕 World Cuisine', '🧑‍🍳 Cooking & Chefs'] },
  { key: '🎵 Music', subs: ['🎸 Rock & Pop', '🕺 80s & 90s', '🎤 Hip Hop & R&B'] },
  { key: '🌍 Geography', subs: ['🗺️ Capitals & Countries', '🏔️ Nature & Landmarks', '🌎 World Records'] },
  { key: '⚽ Sport', subs: ['⚽ Football', '🎾 Other Sports', '🏅 Olympics'] },
  { key: '🎬 Cinema & TV', subs: ['🎬 Movies', '📺 TV Shows', '🦸 Marvel & DC'] },
  { key: '🧠 General Knowledge', subs: ['🔬 Science', '📚 History', '🎯 Mixed'] },
];

const THEME_COLORS = {
  gold: '#FFD166',
  red: '#EF476F',
  green: '#06D6A0',
  blue: '#3B82F6',
  purple: '#8B5CF6',
};

const emptyPackForm = {
  name_en: '', name_fr: '', name_ar: '', name_es: '',
  description_en: '', description_fr: '', description_ar: '', description_es: '',
  emoji: '📋',
  theme_color: 'gold',
  published_at: new Date().toISOString().split('T')[0],
  featured: false,
};

function AdminPacks({ onMessage, onStatsChange }) {
  // 'list' = browse packs, 'detail' = editing one pack
  const [view, setView] = useState('list');
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPackId, setEditingPackId] = useState(null);
  const [packForm, setPackForm] = useState(emptyPackForm);
  const [saving, setSaving] = useState(false);
  const [packQuestions, setPackQuestions] = useState([]);

  // Add-questions picker state
  const [showPicker, setShowPicker] = useState(false);
  const [pickerCat, setPickerCat] = useState(CATEGORIES[0].key);
  const [pickerSub, setPickerSub] = useState(CATEGORIES[0].subs[0]);
  const [pickerQuestions, setPickerQuestions] = useState([]);

  useEffect(() => { loadPacks(); }, []);
  useEffect(() => {
    if (showPicker) loadPickerQuestions();
  }, [showPicker, pickerCat, pickerSub]);

  const loadPacks = async () => {
    setLoading(true);
    const { data: packsData } = await supabase
      .from('quiz_packs')
      .select('*')
      .order('published_at', { ascending: false });

    const { data: junction } = await supabase
      .from('quiz_pack_questions')
      .select('pack_id');

    const counts = {};
    (junction || []).forEach(j => {
      counts[j.pack_id] = (counts[j.pack_id] || 0) + 1;
    });

    const packsWithCount = (packsData || []).map(p => ({ ...p, qCount: counts[p.id] || 0 }));
    setPacks(packsWithCount);
    setLoading(false);
  };

  const loadPickerQuestions = async () => {
    const { data } = await supabase
      .from('questions')
      .select('*')
      .eq('category', pickerCat)
      .eq('subcategory', pickerSub)
      .order('created_at', { ascending: false });
    setPickerQuestions(data || []);
  };

  const loadPackQuestions = async (packId) => {
    const { data: junction } = await supabase
      .from('quiz_pack_questions')
      .select('question_id, position')
      .eq('pack_id', packId)
      .order('position', { ascending: true });

    if (!junction || junction.length === 0) {
      setPackQuestions([]);
      return;
    }

    const ids = junction.map(j => j.question_id);
    const { data: questions } = await supabase
      .from('questions')
      .select('*')
      .in('id', ids);

    // Preserve order
    const orderedQuestions = ids.map(id => questions.find(q => q.id === id)).filter(Boolean);
    setPackQuestions(orderedQuestions);
  };

  const handleCreateOrUpdatePack = async () => {
    if (!packForm.name_en.trim()) {
      onMessage('Pack name (English) is required', 'error');
      return;
    }
    setSaving(true);

    const payload = {
      name_en: packForm.name_en.trim(),
      name_fr: packForm.name_fr.trim() || null,
      name_ar: packForm.name_ar.trim() || null,
      name_es: packForm.name_es.trim() || null,
      description_en: packForm.description_en.trim() || null,
      description_fr: packForm.description_fr.trim() || null,
      description_ar: packForm.description_ar.trim() || null,
      description_es: packForm.description_es.trim() || null,
      emoji: packForm.emoji || '📋',
      theme_color: packForm.theme_color || 'gold',
      published_at: packForm.published_at ? new Date(packForm.published_at).toISOString() : new Date().toISOString(),
      featured: !!packForm.featured,
    };

    let error;
    let newPackId = editingPackId;
    if (editingPackId) {
      const r = await supabase.from('quiz_packs').update(payload).eq('id', editingPackId);
      error = r.error;
    } else {
      const r = await supabase.from('quiz_packs').insert(payload).select().single();
      error = r.error;
      newPackId = r.data?.id;
    }

    if (error) {
      onMessage('Error: ' + error.message, 'error');
      setSaving(false);
      return;
    }

    onMessage(editingPackId ? '✅ Pack updated' : '✅ Pack created');
    if (!editingPackId && newPackId) {
      // Switch to editing the newly created pack so user can add questions
      setEditingPackId(newPackId);
      setView('detail');
      loadPackQuestions(newPackId);
    } else {
      loadPacks();
    }
    if (onStatsChange) onStatsChange();
    setSaving(false);
  };

  const handleEditPack = (pack) => {
    setPackForm({
      name_en: pack.name_en || '',
      name_fr: pack.name_fr || '',
      name_ar: pack.name_ar || '',
      name_es: pack.name_es || '',
      description_en: pack.description_en || '',
      description_fr: pack.description_fr || '',
      description_ar: pack.description_ar || '',
      description_es: pack.description_es || '',
      emoji: pack.emoji || '📋',
      theme_color: pack.theme_color || 'gold',
      published_at: pack.published_at ? new Date(pack.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      featured: !!pack.featured,
    });
    setEditingPackId(pack.id);
    setView('detail');
    loadPackQuestions(pack.id);
  };

  const handleDeletePack = async (id) => {
    if (!window.confirm('Delete this entire pack? Questions inside are NOT deleted, only the pack itself.')) return;
    const { error } = await supabase.from('quiz_packs').delete().eq('id', id);
    if (error) {
      onMessage('Error: ' + error.message, 'error');
    } else {
      onMessage('🗑️ Pack deleted');
      loadPacks();
      if (onStatsChange) onStatsChange();
    }
  };

  const handleAddQuestionToPack = async (questionId) => {
    if (!editingPackId) return;
    if (packQuestions.some(q => q.id === questionId)) {
      onMessage('Question is already in this pack', 'error');
      return;
    }
    const position = packQuestions.length;
    const { error } = await supabase
      .from('quiz_pack_questions')
      .insert({ pack_id: editingPackId, question_id: questionId, position });
    if (error) {
      onMessage('Error: ' + error.message, 'error');
    } else {
      onMessage('✅ Question added to pack');
      loadPackQuestions(editingPackId);
    }
  };

  const handleRemoveQuestionFromPack = async (questionId) => {
    if (!editingPackId) return;
    const { error } = await supabase
      .from('quiz_pack_questions')
      .delete()
      .eq('pack_id', editingPackId)
      .eq('question_id', questionId);
    if (error) {
      onMessage('Error: ' + error.message, 'error');
    } else {
      loadPackQuestions(editingPackId);
    }
  };

  const handleBackToList = () => {
    setView('list');
    setEditingPackId(null);
    setPackForm(emptyPackForm);
    setPackQuestions([]);
    setShowPicker(false);
    loadPacks();
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getPackStatus = (pack) => {
    const now = new Date();
    const pubDate = new Date(pack.published_at);
    if (pubDate > now) return { label: 'Scheduled', color: C.gold };
    return { label: 'Live', color: C.green };
  };

  const inputStyle = {
    width: '100%',
    background: C.bg,
    color: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 2,
    color: C.textMuted,
    marginBottom: 4,
    display: 'block',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: 16,
    paddingRight: 34,
  };

  // ==========================================
  // LIST VIEW
  // ==========================================
  if (view === 'list') {
    return (
      <>
        {/* Create new pack card */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>➕</span>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Create new pack</h3>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>PACK NAME (EN) <span style={{ color: C.red }}>*</span></label>
            <input
              type="text"
              placeholder="e.g. Christmas Quiz 2026"
              value={packForm.name_en}
              onChange={(e) => setPackForm({ ...packForm, name_en: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>EMOJI</label>
              <input
                type="text"
                value={packForm.emoji}
                onChange={(e) => setPackForm({ ...packForm, emoji: e.target.value })}
                style={{ ...inputStyle, textAlign: 'center', fontSize: 20 }}
              />
            </div>
            <div>
              <label style={labelStyle}>PUBLISH DATE</label>
              <input
                type="date"
                value={packForm.published_at}
                onChange={(e) => setPackForm({ ...packForm, published_at: e.target.value })}
                style={{ ...inputStyle, colorScheme: 'dark' }}
              />
            </div>
          </div>

          <button
            onClick={handleCreateOrUpdatePack}
            disabled={saving || !packForm.name_en.trim()}
            style={{
              width: '100%',
              background: (!saving && packForm.name_en.trim()) ? C.green : 'rgba(255,255,255,0.1)',
              color: (!saving && packForm.name_en.trim()) ? '#000' : C.textMuted,
              border: 'none',
              borderRadius: 10,
              padding: 12,
              fontSize: 14,
              fontWeight: 800,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? '⏳ Creating...' : '✅ Create pack & add questions'}
          </button>

          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8, textAlign: 'center' }}>
            You can edit description, translations, and add questions after creating.
          </div>
        </div>

        {/* Existing packs list */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>All packs</h3>
          <span style={{ fontSize: 12, color: C.textSub }}>{packs.length} pack{packs.length !== 1 ? 's' : ''}</span>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: 30, color: C.textSub, fontSize: 13 }}>Loading...</div>}

        {!loading && packs.length === 0 && (
          <div style={{ textAlign: 'center', padding: 30, color: C.textMuted, fontSize: 13, background: C.card, borderRadius: 10, border: `1px dashed ${C.border}` }}>
            No packs yet. Create your first pack above.
          </div>
        )}

        {!loading && packs.map(p => {
          const status = getPackStatus(p);
          const themeC = THEME_COLORS[p.theme_color] || C.gold;
          return (
            <div key={p.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${themeC}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 28 }}>{p.emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: C.white }}>{p.name_en}</div>
                  {p.featured && <span style={{ fontSize: 10, color: C.gold, fontWeight: 700 }}>⭐ FEATURED</span>}
                </div>
                <div style={{ fontSize: 11, color: C.textSub }}>
                  <span style={{ color: status.color, fontWeight: 700 }}>● {status.label}</span>
                  {' · '}{p.qCount} question{p.qCount !== 1 ? 's' : ''}
                  {' · '}Publish: {formatDate(p.published_at)}
                </div>
              </div>
              <button onClick={() => handleEditPack(p)} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: C.white, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }} aria-label="Edit">✏️</button>
              <button onClick={() => handleDeletePack(p.id)} style={{ background: 'rgba(239,71,111,0.15)', border: 'none', color: C.red, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }} aria-label="Delete">🗑️</button>
            </div>
          );
        })}
      </>
    );
  }

  // ==========================================
  // DETAIL VIEW (editing a pack)
  // ==========================================
  return (
    <>
      <button onClick={handleBackToList} style={{ background: C.card, border: 'none', color: C.textSub, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginBottom: 14 }}>
        ← Back to all packs
      </button>

      {/* Pack metadata form */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, marginBottom: 14 }}>📦 Pack details</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>EMOJI</label>
            <input
              type="text"
              value={packForm.emoji}
              onChange={(e) => setPackForm({ ...packForm, emoji: e.target.value })}
              style={{ ...inputStyle, textAlign: 'center', fontSize: 20 }}
            />
          </div>
          <div>
            <label style={labelStyle}>PACK NAME (EN) <span style={{ color: C.red }}>*</span></label>
            <input
              type="text"
              value={packForm.name_en}
              onChange={(e) => setPackForm({ ...packForm, name_en: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Translations */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>NAME · FR (optional)</label>
          <input type="text" value={packForm.name_fr} onChange={(e) => setPackForm({ ...packForm, name_fr: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>NAME · العربية (optional)</label>
          <input type="text" value={packForm.name_ar} onChange={(e) => setPackForm({ ...packForm, name_ar: e.target.value })} dir="rtl" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>NAME · ES (optional)</label>
          <input type="text" value={packForm.name_es} onChange={(e) => setPackForm({ ...packForm, name_es: e.target.value })} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>DESCRIPTION · EN (optional)</label>
          <input type="text" placeholder="Short tagline shown to hosts" value={packForm.description_en} onChange={(e) => setPackForm({ ...packForm, description_en: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>DESCRIPTION · FR (optional)</label>
          <input type="text" value={packForm.description_fr} onChange={(e) => setPackForm({ ...packForm, description_fr: e.target.value })} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>DESCRIPTION · العربية (optional)</label>
          <input type="text" value={packForm.description_ar} onChange={(e) => setPackForm({ ...packForm, description_ar: e.target.value })} dir="rtl" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>DESCRIPTION · ES (optional)</label>
          <input type="text" value={packForm.description_es} onChange={(e) => setPackForm({ ...packForm, description_es: e.target.value })} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>THEME COLOR</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.keys(THEME_COLORS).map(c => (
              <button
                key={c}
                onClick={() => setPackForm({ ...packForm, theme_color: c })}
                style={{
                  flex: 1,
                  background: THEME_COLORS[c],
                  border: packForm.theme_color === c ? '3px solid #fff' : '3px solid transparent',
                  borderRadius: 8,
                  padding: '14px 0',
                  cursor: 'pointer',
                }}
                aria-label={c}
              />
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>PUBLISH DATE</label>
          <input
            type="date"
            value={packForm.published_at}
            onChange={(e) => setPackForm({ ...packForm, published_at: e.target.value })}
            style={{ ...inputStyle, colorScheme: 'dark' }}
          />
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            Pack only appears to players on/after this date.
          </div>
        </div>

        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="featured-toggle"
            checked={packForm.featured}
            onChange={(e) => setPackForm({ ...packForm, featured: e.target.checked })}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <label htmlFor="featured-toggle" style={{ fontSize: 13, color: C.white, cursor: 'pointer' }}>
            ⭐ Featured (show on top of Latest tab)
          </label>
        </div>

        <button
          onClick={handleCreateOrUpdatePack}
          disabled={saving || !packForm.name_en.trim()}
          style={{
            width: '100%',
            background: (!saving && packForm.name_en.trim()) ? C.green : 'rgba(255,255,255,0.1)',
            color: (!saving && packForm.name_en.trim()) ? '#000' : C.textMuted,
            border: 'none',
            borderRadius: 10,
            padding: 12,
            fontSize: 14,
            fontWeight: 800,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? '⏳ Saving...' : '💾 Save pack details'}
        </button>
      </div>

      {/* Questions in this pack */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Questions in this pack</h3>
        <span style={{ fontSize: 12, color: C.textSub }}>{packQuestions.length} question{packQuestions.length !== 1 ? 's' : ''}</span>
      </div>

      {packQuestions.length === 0 && !showPicker && (
        <div style={{ textAlign: 'center', padding: 20, color: C.textMuted, fontSize: 13, background: C.card, borderRadius: 10, border: `1px dashed ${C.border}`, marginBottom: 12 }}>
          No questions yet. Click "Add questions" below.
        </div>
      )}

      {packQuestions.map((q, idx) => (
        <div key={q.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700, width: 20 }}>{idx + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{q.q_en}</div>
            <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>{q.category} · {q.subcategory} · A: {q.a_en}</div>
          </div>
          <button onClick={() => handleRemoveQuestionFromPack(q.id)} style={{ background: 'rgba(239,71,111,0.15)', border: 'none', color: C.red, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }} aria-label="Remove">✕</button>
        </div>
      ))}

      {/* Add questions section */}
      <button
        onClick={() => setShowPicker(!showPicker)}
        style={{ width: '100%', background: 'rgba(255,209,102,0.15)', border: `1px solid ${C.gold}40`, color: C.gold, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 800, cursor: 'pointer', marginTop: 12, marginBottom: showPicker ? 12 : 30 }}
      >
        {showPicker ? '✕ Close question picker' : '➕ Add questions from bank'}
      </button>

      {showPicker && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 30 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>CATEGORY</label>
              <select
                value={pickerCat}
                onChange={(e) => {
                  setPickerCat(e.target.value);
                  setPickerSub(CATEGORIES.find(c => c.key === e.target.value).subs[0]);
                }}
                style={selectStyle}
              >
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.key}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>SUBCATEGORY</label>
              <select value={pickerSub} onChange={(e) => setPickerSub(e.target.value)} style={selectStyle}>
                {(CATEGORIES.find(c => c.key === pickerCat)?.subs || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {pickerQuestions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 16, color: C.textMuted, fontSize: 12 }}>No questions in this subcategory.</div>
          )}

          {pickerQuestions.map(q => {
            const isInPack = packQuestions.some(pq => pq.id === q.id);
            return (
              <div key={q.id} style={{ background: isInPack ? 'rgba(6,214,160,0.08)' : C.bg, border: `1px solid ${isInPack ? C.green : C.border}`, borderRadius: 8, padding: '8px 10px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.white }}>{q.q_en}</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>A: {q.a_en}</div>
                </div>
                {isInPack ? (
                  <span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>✓ Added</span>
                ) : (
                  <button onClick={() => handleAddQuestionToPack(q.id)} style={{ background: C.green, color: '#000', border: 'none', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ Add</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default AdminPacks;